import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

// --- Indian heavy-haul trailer silhouette (side profile, faces right) ---
export function TruckGlyph({ className = "", style }) {
  return (
    <svg viewBox="0 0 262 100" fill="currentColor" className={className} style={style} aria-hidden="true">
      {/* transformer bushings */}
      <g>
        <rect x="34" y="20" width="6" height="14" />
        <circle cx="37" cy="18" r="5" />
        <rect x="56" y="20" width="6" height="14" />
        <circle cx="59" cy="18" r="5" />
        <rect x="78" y="20" width="6" height="14" />
        <circle cx="81" cy="18" r="5" />
      </g>
      {/* transformer / ODC cargo box */}
      <rect x="20" y="30" width="112" height="34" rx="3" />
      {/* low-bed trailer deck */}
      <rect x="8" y="62" width="150" height="9" rx="2" />
      {/* gooseneck to cab */}
      <path d="M158 62 h16 l12 -10 v19 h-28 z" />
      {/* tractor cab */}
      <path d="M188 34 h30 a8 8 0 0 1 8 8 v29 h-46 v-25 a12 12 0 0 1 8 -12 z" />
      {/* chassis */}
      <rect x="150" y="67" width="82" height="6" />
      {/* wheels */}
      <circle cx="30" cy="80" r="12" />
      <circle cx="58" cy="80" r="12" />
      <circle cx="86" cy="80" r="12" />
      <circle cx="172" cy="80" r="12" />
      <circle cx="206" cy="80" r="12" />
    </svg>
  );
}

// --- Animated highway divider: a road line with moving lane dashes and a
// trailer that drives across as the divider scrolls through the viewport ---
export function HighwayDivider({ dark = false, className = "" }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["-14%", "112%"]);
  const bg = dark ? "bg-[#1A1A18]" : "bg-[#F9F8F6]";
  const line = dark ? "#F9F8F6" : "#1A1A18";

  return (
    <div ref={ref} aria-hidden="true" data-testid="highway-divider" className={`relative overflow-hidden ${bg} ${className}`}>
      <div className="relative h-20 sm:h-24 max-w-7xl mx-auto px-5 sm:px-8">
        {/* road lane markings */}
        <div
          className={reduce ? "" : "animate-lane"}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: "2px",
            transform: "translateY(6px)",
            opacity: 0.22,
            backgroundImage: `repeating-linear-gradient(to right, ${line} 0 26px, transparent 26px 52px)`,
          }}
        />
        {/* driving trailer */}
        <motion.div style={reduce ? undefined : { x }} className="absolute top-1/2 left-0 -translate-y-[62%]">
          <TruckGlyph className="w-28 sm:w-36 h-auto" style={{ color: line }} />
        </motion.div>
      </div>
    </div>
  );
}

// --- "Gear shift" in-view entrance: brakes/accelerates into place (skew + blur) ---
export function GearIn({ children, delay = 0, className = "", as = "div", ...rest }) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  if (reduce) {
    return (
      <MotionTag className={className} {...rest}>
        {children}
      </MotionTag>
    );
  }
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y: 44, scale: 0.96, filter: "blur(6px)", skewX: -2 }}
      whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)", skewX: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}
